import * as React from 'react';
import { whatsappColors, type ConversationItemData } from './types';

interface ConversationListItemProps {
  data: ConversationItemData;
  onClick: () => void;
}

const CheckMarks = ({ isRead }: { isRead: boolean }) => {
  const color = isRead ? whatsappColors.blue : whatsappColors.textTertiary;
  return (
    <svg width="16" height="11" viewBox="0 0 16 11" fill="none">
      <path d="M11.071.653a.75.75 0 0 1 1.06 0l3.536 3.536a.75.75 0 0 1 0 1.06l-8.132 8.133a.75.75 0 0 1-1.06 0L.653 7.56a.75.75 0 0 1 0-1.06l1.06-1.061a.75.75 0 0 1 1.061 0l4.773 4.773 6.364-6.364a.75.75 0 0 1 1.06 0z" fill={color}/>
      <path d="M5.694.653a.75.75 0 0 1 1.06 0l3.537 3.536a.75.75 0 0 1 0 1.06L2.16 13.382a.75.75 0 0 1-1.06 0L.653 12.935a.75.75 0 0 1 0-1.06L5.694.653z" fill={color}/>
    </svg>
  );
};

export function ConversationListItem({ data, onClick }: ConversationListItemProps) {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <button
      onClick={onClick}
      className="w-full text-left px-4 py-3 flex items-center gap-3 transition-all duration-150 focus:outline-none"
      style={{
        backgroundColor: data.isActive ? whatsappColors.activeBg : isHovered ? whatsappColors.hoverBg : whatsappColors.panelBg,
        borderBottom: `1px solid ${whatsappColors.border}`,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Avatar */}
      <div 
        className="w-12 h-12 rounded-full bg-[#dfe5e7] flex items-center justify-center text-lg font-medium shrink-0"
        style={{ color: whatsappColors.textSecondary }}
      >
        {data.contactName[0]?.toUpperCase() || '?'}
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        {/* Name and Time */}
        <div className="flex items-center justify-between gap-2 mb-1">
          <div 
            className="font-medium text-[16px] truncate"
            style={{ color: whatsappColors.textPrimary }}
          >
            {data.contactName}
          </div>
          <div 
            className="text-[12px] shrink-0"
            style={{ color: data.unreadCount > 0 ? whatsappColors.green : whatsappColors.textSecondary }}
          >
            {data.lastMessageTime}
          </div>
        </div>

        {/* Last Message and Badge */}
        <div className="flex items-center justify-between gap-2">
          <div 
            className="text-[14px] truncate flex items-center gap-1.5 min-w-0"
            style={{ color: whatsappColors.textSecondary }}
          >
            {data.isOutbound && (
              <span className="inline-flex items-center shrink-0">
                <CheckMarks isRead={data.isRead} />
              </span>
            )}
            <span className="truncate leading-tight">{data.lastMessage}</span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {data.isWindowClosed && (
              <span 
                className="px-2 py-0.5 rounded-full text-[10px] font-medium"
                style={{ 
                  backgroundColor: '#fee2e2', 
                  color: '#991b1b',
                  border: '1px solid #fecaca'
                }}
                title="Janela encerrada"
              >
                Janela fechada
              </span>
            )}
            {data.unreadCount > 0 && (
              <span 
                className="min-w-[20px] h-5 px-1.5 rounded-full text-white text-[12px] font-medium leading-5 text-center shrink-0"
                style={{ backgroundColor: whatsappColors.unreadBadge }}
              >
                {data.unreadCount}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}
